// One site's page: set-up, changes, alert history, the log, health and subscription.

import { useState } from 'react';
import { api } from '../api.ts';
import type { SiteView } from '../types.ts';
import { Chip, Link, Loading, Problem, Shell, useLoad, useRefreshWhile } from '../ui.tsx';
import { Changes } from './site/Changes.tsx';
import { Alerts } from './site/Alerts.tsx';
import { Health } from './site/Health.tsx';
import { Log } from './site/Log.tsx';
import { Setup } from './site/Setup.tsx';
import { Subscription } from './site/Subscription.tsx';

const TABS = ['Setup', 'Changes', 'Alert history', 'Log', 'Health', 'Subscription'] as const;
type Tab = (typeof TABS)[number];

export function Site({ siteId }: { siteId: string }) {
  const site = useLoad(() => api<SiteView>('GET', `/sites/${siteId}`), [siteId]);
  const [tab, setTab] = useState<Tab>('Setup');
  const s = site.data;
  const waiting = s?.changes.find((c) => c.status === 'waiting');
  // While a change waits for the ops tablet, the page updates itself once it's approved there.
  useRefreshWhile(!!waiting, site.reload);

  return (
    <Shell>
      <Link to="/sites" className="muted small">
        ‹ All sites
      </Link>
      {site.error && <Problem text={site.error} />}
      {!s && site.loading && <Loading />}
      {s && (
        <>
          <div className="head">
            <div className="row wrap" style={{ gap: 14 }}>
              <h1>{s.siteName}</h1>
              {s.switchedOffAt ? <Chip tone="red">Switched off</Chip> : s.live ? <Chip tone="green">Live</Chip> : <Chip tone="amber">{waiting ? 'Waiting for approval on the ops tablet' : 'Setting up'}</Chip>}
              <span className="muted">{s.clientName}</span>
              <span className="muted">· {s.subscriptionText}</span>
            </div>
            <Link to={`/sites/${siteId}/setup/${s.live ? 'zones' : 'map'}`} className="btn">
              {s.live ? 'Prepare a change' : 'Continue set-up'}
            </Link>
          </div>
          <div className="tabs" role="tablist">
            {TABS.map((t) => (
              <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? 'tab on' : 'tab'} onClick={() => setTab(t)}>
                {t}
                {t === 'Changes' && waiting ? ' ·  1 waiting' : ''}
              </button>
            ))}
          </div>
          {tab === 'Setup' && <Setup site={s} reload={site.reload} />}
          {tab === 'Changes' && <Changes site={s} reload={site.reload} />}
          {tab === 'Alert history' && <Alerts siteId={siteId} />}
          {tab === 'Log' && <Log siteId={siteId} />}
          {tab === 'Health' && <Health site={s} />}
          {tab === 'Subscription' && <Subscription site={s} reload={site.reload} />}
        </>
      )}
    </Shell>
  );
}
